import { useEffect, useState } from 'react';
import { useAuth } from '@agentnet/auth';
import { Box, FormControlLabel, Switch } from '@material-ui/core';
import { searchFormTemplatesApi } from 'api/formManagement/form-management-api';
import { SearchFormTemplatesRequest, SearchFormTemplatesResponseItem } from 'api/formManagement/types';
import useAsync from 'hooks/useAsync';
import { useNavPermissions } from 'hooks/useNavPermissions';
import { Redirect } from 'react-router-dom';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import CreateNewForm from './CreateNewForm';
import SearchFormFields, { SearchFormValues } from './SearchFormFields';
import { US_STATE_OPTIONS } from './constants';
import { APIStatus } from 'features/serviceorder/constants';

const FormManagement = () => {
  const { getAccessToken } = useAuth();
  const { addGlobalMsg } = useGlobalMessages();
  const { hasPermission } = useNavPermissions();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showSearchFields, setShowSearchFields] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchFormTemplatesResponseItem[]>([]);
  const [lastSearchPayload, setLastSearchPayload] = useState<SearchFormTemplatesRequest | null>(null);
  const [searchFormValues, setSearchFormValues] = useState<SearchFormValues>({
    formItemNo: '',
    formType: '',
    underwriter: '',
    state: '',
  });
  const [editFormIdentifier, setEditFormIdentifier] = useState<{
    starsItemNumber: string;
    starsItemType: string;
    underwriterCode: string;
  } | null>(null);

  const handleAddForm = () => {
    setEditFormIdentifier(null);
    setShowCreateForm(true);
  };

  const handleEditForm = (row: SearchFormTemplatesResponseItem) => {
    setEditFormIdentifier({
      starsItemNumber: row.starsItemNumber,
      starsItemType: row.starsItemType,
      underwriterCode: row.underwriterCode,
    });
    setShowCreateForm(true);
  };

  const handleFormCreated = () => {
    setShowCreateForm(false);
    setEditFormIdentifier(null);
    // Refresh search results if a search was previously performed
    if (lastSearchPayload) {
      executeSearchFormTemplates(lastSearchPayload);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditFormIdentifier(null);
  };

  const searchFormTemplates = async (
    payload: SearchFormTemplatesRequest,
  ): Promise<SearchFormTemplatesResponseItem[]> => {
    const token = await getAccessToken();
    return searchFormTemplatesApi(payload, token);
  };

  const {
    execute: executeSearchFormTemplates,
    status: searchStatus,
    value: searchValue,
    errors: searchErrors,
  } = useAsync<SearchFormTemplatesResponseItem[]>(searchFormTemplates, false);

  const handleSearch = (values: SearchFormValues) => {
    const selectedState = US_STATE_OPTIONS.find((s) => s.value === values.state);
    const payload: SearchFormTemplatesRequest = {
      starsItemNumber: values.formItemNo || undefined,
      starsItemType: values.formType || undefined,
      stateName: (selectedState ? selectedState.name : values.state) || undefined,
      underwriterCode: values.underwriter || undefined,
    };
    setSearchFormValues(values);
    setLastSearchPayload(payload);
    executeSearchFormTemplates(payload);
  };

  const handleClearSearch = () => {
    setSearchResults([]);
    setLastSearchPayload(null);
    setSearchFormValues({
      formItemNo: '',
      formType: '',
      underwriter: '',
      state: '',
    });
  };

  useEffect(() => {
    if (searchStatus === APIStatus.success) {
      setSearchResults(searchValue ?? []);
    }
    if (searchStatus === APIStatus.error) {
      searchErrors?.forEach((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [searchStatus]);

  if (hasPermission.inventoryManagementViewOnly) {
    return <Redirect to="/admin/policy-number-management" />;
  }

  return (
    <ContentContainer fullWidth>
      {!showCreateForm ? (
        <div>
          <PageHeader
            divider={false}
            title="Form Management"
            contentRight={
              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showSearchFields}
                      onChange={(e) => {
                        if (showSearchFields) {
                          handleClearSearch();
                        }
                        setShowSearchFields(e.target.checked);
                      }}
                      color="primary"
                      data-testid="FormManagement-SearchFieldsToggle"
                    />
                  }
                  label="Search Fields"
                  labelPlacement="start"
                />
                <AgentNetButton
                  variant="contained"
                  color="primary"
                  size="small"
                  plusIcon
                  onClick={handleAddForm}
                  data-testid="FormManagement-AddForm"
                >
                  Add Form
                </AgentNetButton>
              </Box>
            }
          />
          {showSearchFields && (
            <SearchFormFields
              onSearch={handleSearch}
              onClearSearch={handleClearSearch}
              searchResults={searchResults}
              isSearching={searchStatus === APIStatus.pending}
              onEditClick={handleEditForm}
              formValues={searchFormValues}
            />
          )}
        </div>
      ) : (
        <CreateNewForm
          onFormCreated={handleFormCreated}
          onCancel={handleCancel}
          isEditMode={!!editFormIdentifier}
          formIdentifier={editFormIdentifier}
        />
      )}
    </ContentContainer>
  );
};

export default FormManagement;
